import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema } from '../schemas';
import { defaultCertificateSchema } from '../../interfaces/IDefaultCertificate';

// ============================================================
// Body Schemas
// ============================================================

const createDefaultCertificateBodySchema = z.object({
	name: requiredStringSchema,
	language: requiredStringSchema,
	groupName: requiredStringSchema,
	isCommonlyUsed: z.boolean(),
});

const deleteMultipleCertificatesBodySchema = z.object({
	ids: z.array(z.string()).min(1),
});

const certificateIdParamsSchema = z.object({
	certificateId: requiredStringSchema,
});

// ============================================================
// Contract
// ============================================================

/**
 * Default certificates contract - routes under /defaultCertificates prefix.
 *
 * IMPORTANT: Specific paths (/multiple) must come BEFORE parameterized paths (/:certificateId).
 * All routes require trusstorAdmin role.
 */
export const defaultCertificatesContract = defineContract('defaultCertificates', {
	getAllDefaultCertificates: route({
		method: 'GET',
		path: '/',
		query: z.object({ language: z.string().optional() }),
		response: z.array(defaultCertificateSchema),
		operationId: 'getAllDefaultCertificates',
		tags: ['DefaultCertificates'],
		summary: 'Get all default certificates',
	}),
	createDefaultCertificate: route({
		method: 'POST',
		path: '/',
		body: createDefaultCertificateBodySchema,
		response: defaultCertificateSchema,
		operationId: 'createDefaultCertificate',
		tags: ['DefaultCertificates'],
		summary: 'Create a default certificate',
	}),
	deleteMultipleCertificates: route({
		method: 'DELETE',
		path: '/multiple',
		body: deleteMultipleCertificatesBodySchema,
		response: z.string(),
		operationId: 'deleteMultipleCertificates',
		tags: ['DefaultCertificates'],
		summary: 'Delete multiple default certificates',
	}),
	getDefaultCertificateById: route({
		method: 'GET',
		path: '/:certificateId',
		params: certificateIdParamsSchema,
		response: defaultCertificateSchema,
		operationId: 'getDefaultCertificateById',
		tags: ['DefaultCertificates'],
		summary: 'Get default certificate by ID',
	}),
	updateDefaultCertificate: route({
		method: 'PUT',
		path: '/:certificateId',
		params: certificateIdParamsSchema,
		body: defaultCertificateSchema.partial(),
		response: defaultCertificateSchema,
		operationId: 'updateDefaultCertificate',
		tags: ['DefaultCertificates'],
		summary: 'Update a default certificate',
	}),
	deleteCertificate: route({
		method: 'DELETE',
		path: '/:certificateId',
		params: certificateIdParamsSchema,
		response: z.string(),
		operationId: 'deleteCertificate',
		tags: ['DefaultCertificates'],
		summary: 'Delete a default certificate',
	}),
});
